import { useEffect, useRef, useState } from 'react';

/**
 * `window.matchMedia` não existe no jsdom por padrão — checar só
 * `typeof window !== 'undefined'` não basta (já derrubou teste aqui antes).
 * O guard certo mede a própria função, não só a existência de `window`.
 */
export function prefereMovimentoReduzido(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Dedo em tela — a MESMA consulta das regras mobile de `motion.css`
 * (`hover: none` + `pointer: coarse`), e não largura de janela. Sem
 * `matchMedia` (jsdom, pré-render) responde `false`: o comportamento de
 * teste e de build é o do desktop, que é o contrato que os testes travam.
 */
export function ehToque(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
}

/**
 * Detecta a entrada de um elemento no viewport.
 *
 * Base de `<Reveal>`, da linha conectora de "Como funciona" e do contador.
 *
 * ## Duas políticas, e por que a segunda existe
 * Por padrão dispara UMA vez e desconecta. Com `repetir`, o elemento também
 * volta a esconder quando sai da tela e reanima quando volta — a página
 * "respira" enquanto se rola, em vez de virar um documento estático depois da
 * primeira passagem. É o comportamento que o dono pediu, e o mesmo que se
 * mede em landings que usam AOS sem `once`.
 *
 * ## Os limiares são ASSIMÉTRICOS de propósito
 * Aparecer com `threshold` (15% visível) e sumir só com ZERO é o que separa
 * "respira" de "pisca". Fossem simétricos, um bloco parado com 14% na borda
 * ficaria invisível enquanto a pessoa tenta lê-lo — e um bloco mais alto que
 * a tela nunca chega a 15% de proporção, então sumiria para sempre.
 *
 * Por isso o observador recebe DOIS limiares: o 0 responde "saiu inteiro?", o
 * outro responde "entrou o bastante?".
 *
 * Sem `IntersectionObserver` no ambiente (jsdom, navegador muito antigo) ou
 * com `prefers-reduced-motion: reduce`, `emVista` já nasce `true` e nada mais
 * acontece — o conteúdo nunca fica preso escondido esperando um observer que
 * não existe, e quem pediu menos movimento não ganha um elemento sumindo a
 * cada rolagem.
 */
export function useEmVista<T extends HTMLElement>(
  threshold = 0.15,
  repetir = false,
): { ref: (elemento: T | null) => void; emVista: boolean } {
  const elementoRef = useRef<T | null>(null);
  const [emVista, setEmVista] = useState(false);

  useEffect(() => {
    const elemento = elementoRef.current;
    if (!elemento) return undefined;

    if (prefereMovimentoReduzido() || !('IntersectionObserver' in window)) {
      setEmVista(true);
      return undefined;
    }

    /*
      NO TOQUE, a revelação roda UMA vez e desconecta.

      O "respira ao rolar" (repetir) mantém todos os observadores vivos e
      re-esconde cada bloco que sai da tela — que então reanima na volta.
      No desktop isso é o que impede a página de virar documento estático;
      num celular é transição e invalidação de estilo em TODA rolagem, na
      máquina que menos tem sobra. A queixa que motivou: iPhone com rolagem
      e menu travando enquanto BI e Adonai, sem revelação repetida, fluem.
    */
    const repete = repetir && !ehToque();

    const observador = new IntersectionObserver(
      (entradas) => {
        for (const entrada of entradas) {
          /*
            `?? 1` — a proporção que falta conta como TELA CHEIA.

            Navegador de verdade sempre manda `intersectionRatio`, mas um
            ambiente que só informe `isIntersecting` faria `undefined >= 0.15`
            ser falso, e o conteúdo ficaria escondido PARA SEMPRE. O sentido
            do erro importa: o padrão desta casa é que a falha revele o
            conteúdo, nunca que o esconda.

            Não é hipótese — foi o que os testes deste hook pegaram na
            primeira vez que a comparação passou a usar a proporção.
          */
          if ((entrada.intersectionRatio ?? 1) >= threshold) {
            setEmVista(true);
            if (!repete) observador.disconnect();
          } else if (repete && !entrada.isIntersecting) {
            // Saiu INTEIRO da tela. Enquanto houver um pixel visível o
            // elemento fica onde está — some no meio da leitura, nunca.
            setEmVista(false);
          }
        }
      },
      { threshold: [0, threshold] },
    );
    observador.observe(elemento);
    return () => observador.disconnect();
  }, [threshold, repetir]);

  function ref(elemento: T | null): void {
    elementoRef.current = elemento;
  }

  return { ref, emVista };
}
